import clsx from "clsx";
import { locales } from "constants/location";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AgntLogo from "../LandingFooter/components/AgntLogo";
import { LocationSelector } from "../LocationSelector";
import BurgerIcon from "./components/BurgerIcon";
import Navigation from "./components/Navigation";
import { useZoomScale } from "hooks/useZoomScale";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAccount } from "wagmi";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { formatAddress } from "utils";
import { WalletPopover } from "../Header/components/WalletPopover";
import { AttsIcon } from "assets/svgComponents/PresaleBlock/ConnectWallet copy";
import { Menu } from "lucide-react";

const DASHBOARD_LINKS = [
  { label: "Dashboard", to: "/dashboard" },
  { label: "Referrals", to: "/referrals" },
];

type LandingHeaderProps = {
  handleOpen: () => void;
};

const LandingHeader: React.FC<LandingHeaderProps> = ({ handleOpen }) => {
  const { i18n } = useTranslation();
  const [isOpenedOptions, setOpenedOptions] = useState(false);
  const [langLocation, setLangLLocation] = useState(i18n.language);
  const [is4kDisplay, setIs4kDisplay] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;
  const isDashboard = pathname !== "/";

  const { isConnected, address } = useAccount();
  const { openConnectModal } = useConnectModal();

  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
      setLangLLocation(savedLanguage);
    } else {
      localStorage.setItem("language", i18n.language);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateIs4kDisplay = () => setIs4kDisplay(window.innerWidth >= 2160);
    updateIs4kDisplay();
    window.addEventListener("resize", updateIs4kDisplay);
    return () => window.removeEventListener("resize", updateIs4kDisplay);
  }, []);

  const handleSetLocation = (loc: string) => {
    i18n.changeLanguage(loc);
    setLangLLocation(loc);
    localStorage.setItem("language", loc);
    setOpenedOptions(false);
  };

  const { scale } = useZoomScale();

  if (isDashboard) {
    return (
      <header className="px-6 pb-4">
        <div
          className="max-w-7xl mx-auto flex items-center justify-between"
          style={is4kDisplay ? { zoom: scale } : undefined}
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 no-underline"
            style={{ textDecoration: "none" }}
          >
            <motion.div
              className="flex items-center "
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <AttsIcon className="size-14" />
              <span className="font-space-grotesk font-bold text-xl tracking-tight text-white">
                AGNT.Hub
              </span>
            </motion.div>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {DASHBOARD_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={clsx(
                  "text-sm font-medium px-4 py-2 rounded-full transition-all duration-300 no-underline",
                  pathname === link.to
                    ? "text-white bg-white/[0.08]"
                    : "text-white/60 hover:text-white hover:bg-white/[0.05]",
                )}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="https://docs.agnthub.ai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-white/60 hover:text-white px-4 py-2 rounded-full hover:bg-white/[0.05] transition-all duration-300 no-underline"
            >
              Whitepaper
            </a>
          </div>

          {/* Wallet button */}
          <div className="hidden md:flex">
            {isConnected && address ? (
              <WalletPopover>
                <motion.button
                  className="bg-white text-black px-6 py-2.5 rounded-full text-sm font-bold tracking-wide"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                >
                  {formatAddress(address, 4, 3)}
                </motion.button>
              </WalletPopover>
            ) : (
              <motion.button
                onClick={() => openConnectModal?.()}
                className="bg-white text-black px-6 py-2.5 rounded-full text-sm font-bold tracking-wide"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                Connect Wallet
              </motion.button>
            )}
          </div>

          {/* Mobile burger */}
          <button className="md:hidden text-white/80" onClick={handleOpen}>
            <Menu size={24} />
          </button>
        </div>
      </header>
    );
  }

  return (
    <header>
      <div
        className={clsx(
          "relative z-20 max-lg:px-[3.2vw] px-[2.25vw] lg:mb-[3vw] max-lg:mb-[10vw]",
        )}
        style={is4kDisplay ? { zoom: scale } : undefined}
      >
        <div className="bg-[#F3F3F8] z-20 flex items-center rounded-[1.94vw] max-lg:rounded-[6.4vw] after:!rounded-[1.94vw] after:max-lg:!rounded-[6.2vw] justify-between py-3 px-[1.285vw] max-lg:px-[3.2vw] shadow-custom landing-header">
          <a href="/">
            <AgntLogo isHeader={true} />
          </a>

          <div className="flex flex-row gap-[0.53vw] max-lg:gap-[2vw]">
            <LocationSelector
              isOpenedOptions={isOpenedOptions}
              setOpenedOptions={setOpenedOptions}
              location={langLocation}
              setLocation={handleSetLocation}
              locations={locales}
              type={"location"}
            />
            <Navigation />
            <BurgerIcon handleClick={handleOpen} />
          </div>
        </div>
      </div>
    </header>
  );
};

export default LandingHeader;
